import React, { Dispatch, SetStateAction } from "react"
import dayjs from "dayjs"
import {
  DiscountCodeRuleSearchHit,
  DiscountExternalRuleSearchHit,
  DiscountRuleSearchHit
} from "lib/types/generated/graphql-types"
import { RULE_TYPE } from "lib/types/common"

import { Actions, RuleInfo } from "./DiscountRuleTable.styled"

import { EmptyList, Id, Name } from "components/Ui/Table/Shared.styled"

import TableRow from "components/Ui/Table/TableRow"
import ActionButton from "components/Ui/Table/ActionButton"
import TableBlurLoading from "components/Ui/Table/TableBlurLoading"
import Status from "components/Ui/Status"
import DeleteDiscountRule from "./DeleteDiscountRule"

import { ReactComponent as ListTreeIcon } from "images/icons/arrow-up-right-dots.svg"
import { ApolloError } from "@apollo/client"
import ErrorMessage from "../../Ui/Messages/ErrorMessage"
import TableHeader from "../../Ui/Table/TableHeader"
import { useSelector } from "react-redux"
import { selectSidebarCollapsed } from "lib/store/services/ui/selectors"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"

type Props = {
  loading: boolean
  setDiscountRules:
    | Dispatch<SetStateAction<DiscountCodeRuleSearchHit[]>>
    | Dispatch<SetStateAction<DiscountExternalRuleSearchHit[]>>
    | Dispatch<SetStateAction<DiscountRuleSearchHit[]>>
  type: RULE_TYPE
  // eslint-disable-next-line
  discountRules: any
  error: ApolloError | undefined
}

export const DiscountRuleTable = ({
  discountRules,
  setDiscountRules,
  loading,
  type,
  error
}: Props) => {
  const sideBarCollapsed = useSelector(selectSidebarCollapsed)

  const getUrl = (id: string) => {
    switch (type) {
      case RULE_TYPE.CART_RULE:
        return `/discounts/cart-rules/${id}`
      case RULE_TYPE.CODE_RULE:
        return `/discounts/discount-code-rules/${id}`
      case RULE_TYPE.EXTERNAL_RULE:
        return `/discounts/external-rules/${id}`
    }
  }

  const getName = () => {
    switch (type) {
      case RULE_TYPE.CART_RULE:
        return `cart rules`
      case RULE_TYPE.CODE_RULE:
        return `discount code rules`
      case RULE_TYPE.EXTERNAL_RULE:
        return `external rules`
    }
  }

  const tableHeader = (
    <TableHeader iconHeader={true}>
      <RuleInfo>Name</RuleInfo>
      <div className={`${sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}`}>
        Status
      </div>
      <div className="hideOnMediumScreens">Valid from</div>
      <div className="hideOnMediumScreens">Valid to</div>
      <div className="hideOnSmallScreens">Sort order</div>
      <WithAuth requiredGroups={PermissionGroups.discounts.write}><Actions></Actions></WithAuth>
    </TableHeader>
  )

  const getTableRow = (
    discountRule?: DiscountCodeRuleSearchHit | DiscountRuleSearchHit | DiscountExternalRuleSearchHit
  ) => {
    if (discountRule)
      return (
        <TableRow to={getUrl(discountRule.id)}>
          <ListTreeIcon />
          <RuleInfo>
            <div>
              <Name>{discountRule.name} </Name>
              <Id>{discountRule.id} </Id>
            </div>
          </RuleInfo>
          <div className={`${sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}`}>
            {discountRule.isActive ? <Status status="Active" /> : <Status status="Inactive" />}
          </div>
          <div className="hideOnMediumScreens">
            {discountRule.validFrom ? dayjs(discountRule.validFrom).format("YYYY-MM-DD") : "-"}
          </div>
          <div className="hideOnMediumScreens">
            {discountRule.validTo ? dayjs(discountRule.validTo).format("YYYY-MM-DD") : "-"}
          </div>
          <div className="hideOnSmallScreens">{discountRule.sortOrder}</div>
          <WithAuth requiredGroups={PermissionGroups.discounts.write}>
            <Actions>
              <DeleteDiscountRule
                discountRule={discountRule}
                discountRules={discountRules}
                setDiscountRules={setDiscountRules}
                type={type}
              />
            </Actions>
          </WithAuth>
        </TableRow>
      )
    return (
      <>
        <ListTreeIcon />
        <RuleInfo>
          <div>
            <Name>Discount rule name</Name>
            <Id>b40c453e-bc09-4d07-b031-825cb4d89b3d</Id>
          </div>
        </RuleInfo>
        <div>
          <Status status="Default" />
        </div>
        <div>2000-00-00</div>
        <div>2000-00-00</div>
        <div>1</div>
        <Actions>
          <ActionButton mode="delete" />
        </Actions>
      </>
    )
  }

  if (loading)
    return <TableBlurLoading numberOfRows={10} tableHeader={tableHeader} rows={getTableRow()} />
  if (error)
    return (
      <ErrorMessage
        showRefreshButton
        message={
          <>
            {`Error loading ${getName()}. Try refreshing the page, or contact `}
            <a href="mailto:support@brinkcommerce.com">support</a>.
          </>
        }
      />
    )

  return discountRules?.length > 0 ? (
    <>
      {tableHeader}
      {discountRules.map(
        (
          discountRule:
            | DiscountCodeRuleSearchHit
            | DiscountRuleSearchHit
            | DiscountExternalRuleSearchHit
        ) => (
          <div key={discountRule.id}>
            {getTableRow(discountRule)}
          </div>
        )
      )}
    </>
  ) : (
    <>
      {!loading && (
        <EmptyList>
          <ListTreeIcon />
          <p> No discount rules found</p>
        </EmptyList>
      )}
    </>
  )
}
